import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ModalData } from '@modules/modal/interfaces';
import { ModalService } from '@modules/modal/services/modal.service';
import {
  filter,
  mergeMap,
  Observable,
  of,
  Subscription,
  interval, firstValueFrom,
} from 'rxjs';

import { InfoModalComponent } from '@components/info-modal/info-modal.component';
import { LoginUserDataI } from '@models/auth';
import { appStateStore } from '@shared/stores/app-state.store';

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  private timerSubscription: Subscription;

  private readonly stateStore = inject(appStateStore);

  remainingTime = signal(+this.stateStore.appState.sessionTimeout());

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly modalService: ModalService,
  ) {
  }

  getVersion() {
    return this.http.post('/core/version', {});
  }

  getURL(data: any): Observable<any> {
    return this.http.post('/user/getURL', data);
  }

  logIn(data: any): Observable<LoginUserDataI> {
    return this.http.post<LoginUserDataI>('/user/login', data);
  }

  updateRemainingTime(): void {
    if (!this.stateStore.appState.sessionTimeout()) return;
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }

    this.remainingTime.set(+this.stateStore.appState.sessionTimeout());
    this.checkSessionTimeout();
  }

  checkSessionTimeout() {
    let modal: MatDialogRef<InfoModalComponent, any>;

    this.timerSubscription = interval(60000)
      .pipe(
        mergeMap(() => {
          if (this.remainingTime() == 3) {
            this.remainingTime.update(time => time - 1);
            modal = this.returnModal();
            return modal.afterClosed().pipe(
              filter(Boolean),
              mergeMap(() => this.getVersion()),
            );
          }
          if (this.remainingTime() == 1) {
            this.stateStore.removeState();
            this.router.navigate(['/']);
            if (modal)
              modal.close();
            this.showModalInfo();
            this.timerSubscription.unsubscribe();
            return of(null);
          } else {
            this.remainingTime.update(time => time - 1);
            return of(null);
          }
        }),
      )
      .subscribe();
  }

  async checkForAuthentication() {
    if (this.stateStore.appState.token()) {
      try {
        const response = await firstValueFrom(
          this.http.post<any>('/user/session', {}, { observe: 'response' }),
        );

        return response.body?.result?.resultCode === 200;
      } catch (error) {
        return false;
      }
    }

    return false;
  }

  returnModal() {
    return this.modalService
      .infoModal(
        new ModalData(
          'modal.session',
          'modal.sessionTime',
          false,
          false),
        true);
  }

  showModalInfo() {
    this.modalService.infoModal(new ModalData(
      'modal.whoops',
      'modal.sessionTimeOf',
      false));
  }
}
